import { ReactNode, createContext, useContext, useMemo } from 'react';
import { Element, Icon } from 'react-bulma-components';
import { FaChevronRight } from 'react-icons/fa6';
import If from '../../../shared/if/if';
import { onEnterOrSpaceKey } from '../../../shared/utils';
import styles from './inbox-item.module.scss';

export type InboxItemProps = {
  children: ReactNode;
  onClick: () => void;
  active: boolean;
  /**
   * The content has controls of its own (icon buttons). Interactive elements
   * cannot nest, so the item is not a button then: its `InboxItem.Label` is.
   * A click anywhere still selects it.
   */
  withControls?: boolean;
};

type InboxItemContextType = Pick<InboxItemProps, 'onClick' | 'active'>;

const InboxItemContext = createContext<InboxItemContextType>({
  onClick: () => {},
  active: false,
});

/** What makes an element the item's button: role, state and keys. */
function itemButtonProps({ active, onClick }: InboxItemContextType) {
  return {
    role: 'button' as const,
    'aria-pressed': active,
    tabIndex: 0,
    onKeyDown: onEnterOrSpaceKey(onClick),
  };
}

export function InboxItem({
  children,
  onClick,
  active,
  withControls = false,
}: InboxItemProps) {
  const containerClassName = useMemo(() => {
    const defaultClassName = styles.container;
    const activeClassName = styles.active;
    return active ? `${defaultClassName} ${activeClassName}` : defaultClassName;
  }, [active]);

  return (
    <InboxItemContext.Provider value={{ onClick, active }}>
      <Element
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        py={4}
        px={2}
        className={containerClassName}
        onClick={onClick}
        {...(!withControls && itemButtonProps({ active, onClick }))}
      >
        <Element display="flex">{children}</Element>
        <If condition={active}>
          <Icon>
            <FaChevronRight />
          </Icon>
        </If>
      </Element>
    </InboxItemContext.Provider>
  );
}

type InboxItemLabelProps = {
  children: ReactNode;
  className?: string;
};

/** The part that selects an item `withControls`. */
const InboxItemLabel = ({ children, className }: InboxItemLabelProps) => {
  const item = useContext(InboxItemContext);

  return (
    <span className={className} {...itemButtonProps(item)}>
      {children}
    </span>
  );
};

InboxItem.Label = InboxItemLabel;

export default InboxItem;
